import React, { useEffect, useMemo } from 'react';
import classnames from 'classnames';
import { history, connect, useIntl } from 'umi';
import { ConfigProvider, Layout, Segment } from 'tea-component';
import Loading from '@/pages/dashboard/components/Loadings';
import LeftMenu from '@/pages/home/components/LeftMenu';
import MenuHeader from '@/pages/benchmark/components/LeftMenu/components/MenuHeader';
import { LANGUAGE_EN } from '@home/_config/config';
import { selectValByCondition } from '@home/utils/common';
import { getBasePath, storage } from '@/utils/utils';
import { DBB_FRAME_TYPE_CACHE } from '@/pages/benchmark/_config/constant';
import { flattenMenu, getLogTypeId, parseBenchmarkUrlInfo } from '@/pages/benchmark/utils/utils';
import { getPathFromDashboard, matchPathWithDashboard } from '@/pages/dashboard/utils/utils';
import { buriedPoint } from '@home/utils/buriedPoint';
import { WHITE_LIST_OF_ROUTERS } from '@/pages/benchmark/_config/routers';
import { LayoutContext } from '@/pages/benchmark/state/LayoutContext';
import { PAGE_TYPE_OUT_PATH, PAGE_TYPE_OUT_URL } from '@/pages/dashboard/_config/constant';
import { useEmbeddedPageOptions } from '@home/hooks/useEmbeddedPageOptions';
import './index.scss';
const { Body, Content } = Layout;

const BenchmarkLayout = (props: any) => {
  const {
    menuList,
    menuLoading,
    dispatch,
    location,
    explainLoading,
    metricLoading,
    metricConfig,
  } = props;
  const { locale } = useIntl();
  const embedOptions = useEmbeddedPageOptions();
  const isEN = locale === LANGUAGE_EN;
  // benchmark路径解析
  const { system, urlType, realPath } = parseBenchmarkUrlInfo(location.pathname);
  // 框架类型优先级(url路径>本地缓存>默认)
  const frameType = urlType || storage.getItem(DBB_FRAME_TYPE_CACHE) || 'pcconsole';

  // 带 query 参数的完整路径，用于匹配 path 中带 query 的菜单项（如 IAABenchmarks?lowcode_template_key=...）
  const realPathWithSearch = realPath + (location.search || '');

  const {
    options,  // tab切换数据
    selectedTab,  // 选中tabs数据
    tabMenuList,  // 菜单数据
    dashboardList,  // 全量报表数据
    findMenu,  // url匹配的菜单
    currentMenu,  // 选中菜单数据
  } = useMemo(() => {
    if (!menuList) return { options: [], selectedTab: {}, tabMenuList: [], dashboardList: [], findMenu: null, currentMenu: null };
    // 渲染的菜单数据
    const selectedTab = menuList?.find((item: { frameType: string; }) => item?.frameType === frameType) || {};
    const tabMenuList = selectedTab?.subMenus || [];
    // 选中菜单类型下所有报表
    const dashboardList = flattenMenu(selectedTab?.subMenus);
    // 从dashboard集合数组查找匹配的menu（先用不含query的路径匹配，再用含query的路径匹配）
    const findMenu = dashboardList?.find((item: any) => matchPathWithDashboard(item, realPath))
      || dashboardList?.find((item: any) => matchPathWithDashboard(item, realPathWithSearch));
    // 选中菜单信息(选中菜单>默认第一个>大于默认值)
    const currentMenu = findMenu || dashboardList?.[0] || null;
    // 菜单tab切换Options
    const options = menuList?.map((item: { frameType: string; title: string; titleCN: string; }) => {
      const { frameType, title, titleCN } = item || {};
      return {
        value: frameType,
        text: selectValByCondition(isEN, title, titleCN)
      };
    });
    return { options, selectedTab, tabMenuList, dashboardList, findMenu, currentMenu };
  }, [menuList, frameType, realPath, realPathWithSearch, isEN]);

  // 路径重定向和补齐操作
  useEffect(() => {
    // menu 正在请求的时候
    if (menuLoading) {
      return;
    }
    // 没有获取菜单的情况
    if (!menuList) {
      dispatch({
        type: 'benchmark/getLeftMenuList',
        payload: {},
      });
      return;
    }
    // options是否包含该选项，不包含路由重定向
    const include = options?.find((item: { value: string; }) => item?.value === frameType);
    if (include) {
      storage.setItem(DBB_FRAME_TYPE_CACHE, frameType);
    } else {
      // 选中菜单如果有菜单类型,没有则使用菜单第一项
      // 重置frameType路由（选中tab>菜单第一项）
      const type = selectedTab?.frameType || menuList?.[0]?.frameType;
      if (type) {
        history.replace(`/${system}/${type}`);
      } else {
        // 没有框架信息（menuList中没有frameType信息）
        history.replace(`/${system}`);
      }
      return;
    }
    // 没有详细路径的情况,或者当前url和menu不匹配,重定向到第一个dashboard
    if (!realPath || findMenu !== currentMenu) {
      // 查不到的情况，对比白名单中的菜单，如果是白名单不做处理，非白名单跳转到第一个dashbaord
      if (dashboardList?.[0] && !WHITE_LIST_OF_ROUTERS?.includes(realPath)) {
        history.replace(`/${system}/${frameType}/${getPathFromDashboard(dashboardList?.[0])}`);
      }
      return;
    }
  }, [dispatch, menuList, menuLoading, realPath, system, frameType, options, selectedTab, dashboardList, currentMenu, findMenu]);

  // 页面初始化请求
  useEffect(() => {
    dispatch({
      type: 'benchmark/getExplainInfo',
    });
    dispatch({
      type: 'benchmark/getMetricInfo',
    });
  }, [dispatch]);

  // 设置指标配置metricConfig
  useEffect(() => {
    if (explainLoading || metricLoading) {
      return;
    }
    // 设置对应的指标注释和指标中英文
    dispatch({
      type: 'benchmark/setMetricConfig',
      payload: {
        frameType
      },
    });
  }, [dispatch, explainLoading, frameType, metricLoading]);

  // 框架选择类型,相同tab点击无反应
  const menuTypeChange = (value: string) => {
    // if (value === frameType) return;
    history.push(`/benchmark/${value}/${getPathFromDashboard(currentMenu)}`);
  };

  // 点击选中菜单
  const clickMenu = (menu: any) => {
    const { id = '', name_en: nameEn, name_cn: nameCn, url, u_id: uId } = menu || {};
    const logObj = {
      menuId: id,
      pageName: nameEn || nameCn,
      extInfo: nameEn || nameCn,
      pagePath: url,
      typeId: getLogTypeId(frameType),
      uidType: '', // 基准打点不包含uidType
    };
    // 如果菜单有 NEW 标记，上报已读
    if (menu?.showNew && uId) {
      dispatch({
        type: 'benchmark/cancelMenuNewStatus',
        payload: { uId },
      });
    }
    // out path打开系统path
    if (menu.page_type === PAGE_TYPE_OUT_PATH) {
      buriedPoint({ ...logObj, pageId: '801101' });
      window.open(`${getBasePath()}${url}`);
      return false;
    }
    // out url打开外部url
    if (menu.page_type === PAGE_TYPE_OUT_URL) {
      buriedPoint({ ...logObj, pageId: '801201' });
      window.open(`${url}`);
      return false;
    }
    history.push(`/benchmark/${frameType}/${getPathFromDashboard(menu)}`);
    // 公共菜单组件菜单点击事件返回false中止后续操作(用于解决公共组件固定菜单路径跳转)
    return false;
  };

  // 异常处理,内容中的loading和无权限
  const contentErrorInfo = useMemo(() => {
    // 菜单menuLoading 指标解释loading 指标配置loading
    if (menuLoading || !menuList || explainLoading || metricLoading) {
      return <Loading />;
    }
    return null;
  }, [menuLoading, menuList, explainLoading, metricLoading]);

  return (
    <ConfigProvider locale={locale === "en-US" ? "en" : "zh"}>
      <LayoutContext.Provider value={{ frameType, currentMenu, metricConfig }}>
        <Body
          className={classnames('layout__body dbb-body', {
            'dbh-embed-hide-page-header': embedOptions.hidePageHeader,
            'dbh-embed-hide-page-tabs': embedOptions.hidePageTabs,
          })}
        >
          {menuLoading ? (
            <Loading />
          ) : (<>
            {!embedOptions.hideLeftMenu && <LeftMenu
              title={"Benchmark"}
              titleCN={"行业基准"}
              clickMenu={clickMenu}
              currentPath={getPathFromDashboard(currentMenu)}
              customHeaderElement={
                <MenuHeader
                  selectedTab={selectedTab}
                >
                  <Segment
                    value={frameType}
                    onChange={menuTypeChange}
                    options={options}
                  />
                </MenuHeader>
              }
              menuList={tabMenuList || []}
            />}
            <Content key={frameType}>
              {contentErrorInfo || props.children}
            </Content>
          </>
          )}
        </Body>
      </LayoutContext.Provider>
    </ConfigProvider>
  );
};

export default connect((state: any) => {
  const { loading, benchmark } = state;
  return {
    menuList: benchmark.menuList,
    metricConfig: benchmark.metricConfig,
    menuLoading: loading.effects['benchmark/getLeftMenuList'],
    explainLoading: loading.effects['benchmark/getExplainInfo'],
    metricLoading: loading.effects['benchmark/getMetricInfo'],
  };
})(BenchmarkLayout);
